import React, { useState } from "react";
import * as XLSX from "xlsx";
import api from "../services/api";
import { themeDefaults } from "../constants/themeDefaults";
import "./MariageOptionsModal.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mariage: any;
  onUpdated: () => void;
}

const THEMES = [
  { value: "ClassiqueHero", label: "🏛️ Noir Doux & Or Mat", family: "CLASSIQUE" },
  { value: "BeigeMarron", label: "🏛️ Beige & Brun Doux", family: "CLASSIQUE" },
  { value: "CarminRose", label: "🏛️ Rouge Profond Doux", family: "CLASSIQUE" },
  
  { value: "ModerneHero", label: "🏙️ Champagne Clair", family: "MODERNE" },
  { value: "VertForet", label: "🏙️ Bleu Roi Calme", family: "MODERNE" },
  { value: "BleuNuit", label: "🏙️ Bleu Nuit Doux", family: "MODERNE" },
  
  { value: "ElegantHero", label: "✨ Élégant Hero", family: "ÉLÉGANT" },
  { value: "MauveSaphir", label: "✨ Mauve Poudré Doux", family: "ÉLÉGANT" },
  { value: "BordeauxPeche", label: "✨ Bordeaux Élégant", family: "ÉLÉGANT" },
  { value: "RoseBordeaux", label: "✨ Rose Pâle Calme", family: "ÉLÉGANT" },
  
  { value: "TempleHero", label: "🕍 Temple Ardoise", family: "TEMPLE" },
  { value: "TempleNoir", label: "🕍 Temple Graphite", family: "TEMPLE" },
  { value: "TempleSable", label: "🕍 Temple Plomb", family: "TEMPLE" },
];

const THEME_COLORS: Record<string, string[]> = {
  ClassiqueHero: ["#121212", "#c2a46d", "#e8e6e3"],
  BeigeMarron: ["#3b312c", "#cbb7a1", "#f0ebe5"],
  CarminRose: ["#4a1f28", "#d6b0a0", "#f3ece8"],
  
  ModerneHero: ["#f5f1ea", "#bfa37a", "#3a3a3a"],
  VertForet: ["#1f2a38", "#c7cbd6", "#e6e9ef"],
  BleuNuit: ["#0f1c2b", "#d2c2a8", "#e9e6e1"],
  
  ElegantHero: ["#002c3c", "#ff4d6d", "#c49f59"],
  MauveSaphir: ["#2b2430", "#c8b6c8", "#e7e3ea"],
  BordeauxPeche: ["#3a1f24", "#d4b8a3", "#f1eae4"],
  RoseBordeaux: ["#3c2529", "#d8b4b4", "#f2ecec"],
  
  TempleHero: ["#2a3a4a", "#c9b8a8", "#f0e8e0"],
  TempleNoir: ["#2a2a2a", "#b8a898", "#f0e8e0"],
  TempleSable: ["#3a3a3a", "#d4b8a8", "#f0e8e0"],
};

interface ApiResponse {
  success: boolean;
  message?: string;
  count?: number;
  skipped?: number;
  couples?: number;
  individus?: number;
}

const MariageOptionsModal: React.FC<Props> = ({ isOpen, onClose, mariage, onUpdated }) => {
  const [editMode, setEditMode] = useState(false);
  const [ajoutInvMode, setAjoutInvMode] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<any>({
    template: mariage.template,
    bride: mariage.bride,
    groom: mariage.groom,
    jourSemaine: mariage.jourSemaine,
    date: mariage.date,
    heure: mariage.heure,
    dateISO: mariage.dateISO,
    heureISO: mariage.heureISO,
    tableName: mariage.tableName,
    numeroMarie: mariage.numeroMarie,
    mapEmbedUrl: mariage.mapEmbedUrl,
    background: mariage.theme?.background,
    heart: mariage.theme?.heart,
    text: mariage.theme?.text,
    accent: mariage.theme?.accent,
    overlay: mariage.theme?.overlay,
    box: mariage.theme?.box,
    accentLine: mariage.theme?.accentLine,
    inviteBoxBackground: mariage.theme?.inviteBoxBackground,
    sectionsBackground: mariage.theme?.sectionsBackground,
    boissonsSansAlcool: mariage.boissonsSansAlcool || [],
    boissonsAvecAlcool: mariage.boissonsAvecAlcool || [],
    photoBackground: null,
    invitationImage: null,
    familleMarie: mariage.familleMarie || "",
    familleMariee: mariage.familleMariee || "",
    lieuReception: mariage.lieuReception || "",
    adresseTexte: mariage.adresseTexte || "",
    citation: mariage.citation || "",
    referenceCitation: mariage.referenceCitation || "",
    programme1: mariage.programme1 || "",
    programme2: mariage.programme2 || "",
    posteMarie: mariage.posteMarie || "",
    posteMariee: mariage.posteMariee || "",
    phraseUnion: mariage.phraseUnion || ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === "template") {
      const newThemeColors = themeDefaults[value] || themeDefaults.ClassiqueHero;
      
      setFormData((prev: any) => ({ 
        ...prev, 
        [name]: value,
        background: newThemeColors.background,
        heart: newThemeColors.heart,
        text: newThemeColors.text,
        accent: newThemeColors.accent,
        overlay: newThemeColors.overlay,
        box: newThemeColors.box,
        accentLine: newThemeColors.accentLine,
        inviteBoxBackground: newThemeColors.inviteBoxBackground,
        sectionsBackground: newThemeColors.sectionsBackground,
      }));
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleArrayChange = (name: string, index: number, value: string) => {
    const newList: string[] = [...formData[name]];
    newList[index] = value;
    setFormData({ ...formData, [name]: newList });
  };

  const addArrayItem = (name: string) => {
    setFormData({ ...formData, [name]: [...formData[name], ""] });
  };

  const removeArrayItem = (name: string, index: number) => {
    const newList: string[] = formData[name].filter((_: string, i: number) => i !== index);
    setFormData({ ...formData, [name]: newList });
  };

  const handleSubmit = async () => {
    const data = new FormData();

    data.append("template", formData.template);
    data.append("bride", formData.bride);
    data.append("groom", formData.groom);
    data.append("jourSemaine", formData.jourSemaine);
    data.append("date", formData.date);
    data.append("heure", formData.heure);
    data.append("dateISO", formData.dateISO);
    data.append("heureISO", formData.heureISO);
    data.append("tableName", formData.tableName);
    data.append("numeroMarie", formData.numeroMarie);
    data.append("mapEmbedUrl", formData.mapEmbedUrl);
    
    data.append("familleMarie", formData.familleMarie);
    data.append("familleMariee", formData.familleMariee);
    data.append("lieuReception", formData.lieuReception);
    data.append("adresseTexte", formData.adresseTexte);
    data.append("citation", formData.citation);
    data.append("referenceCitation", formData.referenceCitation);
    data.append("programme1", formData.programme1);
    data.append("programme2", formData.programme2);
    data.append("posteMarie", formData.posteMarie);
    data.append("posteMariee", formData.posteMariee);
    data.append("phraseUnion", formData.phraseUnion);

    const themeObject = {
      background: formData.background,
      heart: formData.heart,
      text: formData.text,
      accent: formData.accent,
      overlay: formData.overlay,
      box: formData.box,
      accentLine: formData.accentLine,
      inviteBoxBackground: formData.inviteBoxBackground,
      sectionsBackground: formData.sectionsBackground,
    };
    
    data.append("theme", JSON.stringify(themeObject));

    formData.boissonsSansAlcool.forEach((b: string) => data.append("boissonsSansAlcool", b));
    formData.boissonsAvecAlcool.forEach((b: string) => data.append("boissonsAvecAlcool", b));

    if (formData.photoBackground instanceof File) {
      data.append("photoBackground", formData.photoBackground);
    }
    if (formData.invitationImage instanceof File) {
      data.append("invitationImage", formData.invitationImage);
    }

    try {
      await api.put(`/mariages/${mariage._id}`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setEditMode(false);
      onClose();
      onUpdated();
    } catch (error) {
      console.error("Erreur mise à jour:", error);
      alert("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("⚠️ Es-tu sûr de vouloir supprimer ce mariage ?");
    if (!confirmed) return;
    
    try {
      await api.delete(`/mariages/${mariage._id}`);
      onClose();
      onUpdated();
    } catch (error) {
      console.error("Erreur suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  // Ajout de la fonction pour activer/désactiver le livre d'or
  const handleToggleLivreOr = async () => {
    const newValue = !mariage.livreOrActive;
    const confirmed = window.confirm(`${newValue ? "📖 Activer" : "🔒 Désactiver"} le livre d'or pour ce mariage ?`);
    if (!confirmed) return;
    
    try {
      const formData = new FormData();
      formData.append("livreOrActive", newValue.toString());
      await api.put(`/mariages/${mariage._id}`, formData);
      alert(`✅ Livre d'or ${newValue ? "activé" : "désactivé"} avec succès !`);
      onUpdated();
      onClose();
    } catch (error) {
      console.error("Erreur:", error);
      alert("❌ Erreur lors de la modification du livre d'or");
    }
  };

  const readExcelFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0] as string[];
            const rows = jsonData.slice(1);
            
            const formattedData = rows.map((row: any) => {
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = row[index] || "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const readCSVFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          
          if (lines.length > 0) {
            let separator = '\t';
            if (lines[0].includes(',')) separator = ',';
            if (lines[0].includes(';')) separator = ';';
            
            const headers = lines[0].split(separator).map(h => h.trim());
            const rows = lines.slice(1);
            
            const formattedData = rows.map((line) => {
              const values = line.split(separator);
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header && header.trim() !== "") {
                  obj[header.trim()] = values[index] ? values[index].trim() : "";
                }
              });
              return obj;
            }).filter(row => Object.keys(row).length > 0);
            
            resolve(formattedData);
          } else {
            resolve([]);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsText(file, 'UTF-8');
    });
  };

  const downloadExcelTemplate = () => {
    const wb = XLSX.utils.book_new();
    
    const wsData = [
      ["Nom", "Table", "Couple", "Téléphone"]
    ];
    
    for (let i = 0; i < 20; i++) {
      wsData.push(["", "", "", ""]);
    }
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const wscols = [
      { wch: 30 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 }
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invités");
    
    const fileName = `invites-${mariage.bride}-${mariage.groom}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
      alert("Veuillez sélectionner un fichier Excel (.csv, .xlsx, .xls)");
      return;
    }

    setExcelFile(file);
  };

  const handleCreateInvitations = async () => {
    if (!excelFile) {
      alert("Veuillez d'abord sélectionner un fichier Excel");
      return;
    }

    setLoading(true);

    try {
      let guestsData: any[] = [];
      
      if (excelFile.name.endsWith('.csv')) {
        guestsData = await readCSVFile(excelFile);
      } else if (excelFile.name.match(/\.(xlsx|xls)$/i)) {
        guestsData = await readExcelFile(excelFile);
      } else {
        alert("Format de fichier non supporté");
        return;
      }

      if (guestsData.length === 0) {
        alert("Aucune donnée trouvée dans le fichier");
        return;
      }

      const normalizedData = guestsData.map((guest) => {
        const nom = guest.Nom || guest.NOM || guest.nom || guest['Nom complet'] || guest.Name || '';
        const table = guest.Table || guest.TABLE || guest.table || guest['Numéro table'] || guest['Table numéro'] || '';
        const couple = guest.Couple || guest.COUPLE || guest.couple || guest['Est couple'] || guest['Couple?'] || '';
        const telephone = guest.Téléphone || guest.TELEPHONE || guest.telephone || 
                         guest.Tél || guest.tel || guest['Numéro téléphone'] || guest.Phone || '';

        let normalizedCouple = couple.toString().trim().toLowerCase();
        
        if (normalizedCouple !== "" && normalizedCouple !== "oui" && normalizedCouple !== "non") {
          normalizedCouple = "non";
        }
        
        if (normalizedCouple === "") {
          normalizedCouple = "non";
        }

        let normalizedPhone = telephone.toString().trim();
        normalizedPhone = normalizedPhone.replace(/\s+/g, '');
        normalizedPhone = normalizedPhone.replace(/[^\d+]/g, '');
        
        if (normalizedPhone && !normalizedPhone.startsWith('+')) {
          normalizedPhone = '+243' + normalizedPhone;
        }

        return {
          nom: nom.toString().trim(),
          table: table.toString().trim(),
          couple: normalizedCouple,
          telephone: normalizedPhone
        };
      }).filter(guest => guest.nom !== "");

      if (normalizedData.length === 0) {
        alert("Aucune donnée valide après traitement");
        return;
      }

      const couplesCount = normalizedData.filter(guest => guest.couple === "oui").length;
      const individusCount = normalizedData.filter(guest => guest.couple === "non").length;

      const response = await api.post<ApiResponse>(`/mariages/${mariage._id}/invites/batch`, {
        invites: normalizedData.map(guest => ({
          nom: guest.nom,
          table: guest.table,
          couple: guest.couple,
          telephone: guest.telephone,
          statut: "pending",
          mariageId: mariage._id
        }))
      });

      if (response.data.success) {
        alert(`${normalizedData.length} invitations créées !\n${couplesCount} couples, ${individusCount} individus`);
        setExcelFile(null);
        setAjoutInvMode(false);
        onUpdated();
      } else {
        alert(`Erreur: ${response.data.message || "Impossible de créer les invitations"}`);
      }

    } catch (error: any) {
      console.error("Erreur:", error);
      alert(error.response?.data?.message || "Une erreur est survenue lors de la lecture du fichier");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content scrollable">
        {!editMode && !ajoutInvMode ? (
          <>
            <h3>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
              </svg>
              Options pour {mariage.bride} & {mariage.groom}
            </h3>
            <ul className="options-list">
              <li>
                <button onClick={() => setEditMode(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                  </svg>
                  Modifier
                </button>
              </li>
              <li>
                <button onClick={handleDelete}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                  </svg>
                  Supprimer
                </button>
              </li>
              <li>
                <button onClick={() => setAjoutInvMode(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  </svg>
                  Ajouter des invités
                </button>
              </li>
              {/* NOUVEAU BOUTON POUR LE LIVRE D'OR */}
              <li>
                <button onClick={handleToggleLivreOr} style={{
                  background: mariage.livreOrActive ? "rgba(76, 175, 80, 0.1)" : "rgba(244, 67, 54, 0.1)",
                  border: `1px solid ${mariage.livreOrActive ? "#4caf50" : "#f44336"}`
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: mariage.livreOrActive ? "#4caf50" : "#f44336" }}>
                    <path d="M4 6h16v2H4V6zm2-4h12v2H6V2zm16 8H2v12h20V10zm-2 10H4v-8h16v8z" fill="currentColor"/>
                  </svg>
                  {mariage.livreOrActive ? "✓ Livre d'or activé" : "✗ Livre d'or désactivé"}
                </button>
              </li>
            </ul>
            <button className="close-btn" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
              </svg>
              Fermer
            </button>
          </>
        ) : ajoutInvMode ? (
          <>
            <h3>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z" fill="currentColor"/>
              </svg>
              Ajouter des invités
            </h3>
            <div className="form-scroll">
              <div className="excel-section">
                <button onClick={downloadExcelTemplate} className="excel-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                  </svg>
                  Télécharger modèle
                </button>
                
                <div className="upload-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor"/>
                  </svg>
                  Sélectionner fichier
                  <input 
                    type="file" 
                    accept=".csv,.xlsx,.xls"
                    onChange={handleExcelUpload}
                    disabled={loading}
                  />
                </div>
                
                {excelFile && (
                  <div className="file-info">
                    <p>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                      </svg>
                      Fichier : {excelFile.name}
                    </p>
                    <button 
                      onClick={handleCreateInvitations} 
                      className="create-btn"
                      disabled={loading}
                    >
                      {loading ? "Traitement..." : "Créer les invitations"}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button className="close-btn" onClick={() => {
              setAjoutInvMode(false);
              setExcelFile(null);
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
              </svg>
              Retour
            </button>
          </>
        ) : (
          <>
            <h3>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
              </svg>
              Modifier le mariage
            </h3>
            
            {formData.template && THEME_COLORS[formData.template] && (
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                padding: '15px', 
                background: '#f5f5f5', 
                borderRadius: '8px',
                marginBottom: '20px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                {THEME_COLORS[formData.template].map((color, index) => (
                  <div key={index} style={{ 
                    width: '40px', 
                    height: '40px', 
                    backgroundColor: color,
                    borderRadius: '6px',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }} />
                ))}
                <div style={{ marginLeft: '10px' }}>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {THEMES.find(t => t.value === formData.template)?.label || formData.template}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>
                    Famille: {THEMES.find(t => t.value === formData.template)?.family}
                  </p>
                </div>
              </div>
            )}

            <div className="form-scroll">
              <div className="form-group">
                <label>Template</label>
                <select 
                  name="template" 
                  value={formData.template} 
                  onChange={handleChange}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  <optgroup label="🏛️ FAMILLE CLASSIQUE" style={{ fontWeight: 'bold', color: '#8B4513' }}>
                    {THEMES.filter(t => t.family === "CLASSIQUE").map(theme => (
                      <option key={theme.value} value={theme.value}>
                        {theme.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="🏙️ FAMILLE MODERNE" style={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {THEMES.filter(t => t.family === "MODERNE").map(theme => (
                      <option key={theme.value} value={theme.value}>
                        {theme.label}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="✨ FAMILLE ÉLÉGANT" style={{ fontWeight: 'bold', color: '#800080' }}>
                    {THEMES.filter(t => t.family === "ÉLÉGANT").map(theme => (
                      <option key={theme.value} value={theme.value}>
                        {theme.label}
                      </option>
                    ))}
                  </optgroup>

                  <optgroup label="🕍 FAMILLE TEMPLE" style={{ fontWeight: 'bold', color: '#2a3a4a' }}>
                    {THEMES.filter(t => t.family === "TEMPLE").map(theme => (
                      <option key={theme.value} value={theme.value}>
                        {theme.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Mariée (prénom)</label>
                  <input type="text" name="bride" value={formData.bride} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Marié (prénom)</label>
                  <input type="text" name="groom" value={formData.groom} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom de famille de la mariée *</label>
                  <input 
                    type="text" 
                    name="posteMariee" 
                    value={formData.posteMariee} 
                    onChange={handleChange} 
                    placeholder="ex: KAVIRA"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de famille du marié *</label>
                  <input 
                    type="text" 
                    name="posteMarie" 
                    value={formData.posteMarie} 
                    onChange={handleChange} 
                    placeholder="ex: KABONGO"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Jour semaine</label>
                  <input type="text" name="jourSemaine" value={formData.jourSemaine} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" name="date" value={formData.date} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Heure</label>
                  <input type="time" name="heure" value={formData.heure} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Date ISO</label>
                  <input type="text" name="dateISO" value={formData.dateISO} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Heure ISO</label>
                  <input type="text" name="heureISO" value={formData.heureISO} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Table</label>
                  <input type="text" name="tableName" value={formData.tableName} onChange={handleChange} />
                </div>
              </div>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                </svg>
                Informations pour l'invitation
              </h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Famille du marié</label>
                  <input type="text" name="familleMarie" value={formData.familleMarie} onChange={handleChange} placeholder="ex: MUPIRA" />
                </div>
                <div className="form-group">
                  <label>Famille de la mariée</label>
                  <input type="text" name="familleMariee" value={formData.familleMariee} onChange={handleChange} placeholder="ex: TSHISWEKA" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lieu de réception</label>
                  <input type="text" name="lieuReception" value={formData.lieuReception} onChange={handleChange} placeholder="ex: SALLE JUSTE IMAGINE" />
                </div>
                <div className="form-group">
                  <label>Adresse (texte)</label>
                  <input type="text" name="adresseTexte" value={formData.adresseTexte} onChange={handleChange} placeholder="ex: N° 19, AVENUE L'HAUT FA CHIN, GOLF" />
                </div>
              </div>

              <div className="form-group">
                <label>Citation</label>
                <textarea 
                  name="citation" 
                  value={formData.citation} 
                  onChange={handleChange} 
                  placeholder="ex: « Celui qui trouve une femme trouve le bonheur... »" 
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Référence citation</label>
                  <input type="text" name="referenceCitation" value={formData.referenceCitation} onChange={handleChange} placeholder="ex: Proverbe 18:22" />
                </div>
                <div className="form-group">
                  <label>Programme 1</label>
                  <input type="text" name="programme1" value={formData.programme1} onChange={handleChange} placeholder="ex: MARIAGE CIVIL ET COUTUMIER" />
                </div>
              </div>

              <div className="form-group">
                <label>Programme 2</label>
                <input type="text" name="programme2" value={formData.programme2} onChange={handleChange} placeholder="ex: CÉLÉBRATION RELIGIEUSE" />
              </div>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                </svg>
                Personnalisation
              </h4>

              <div className="form-group">
                <label>Phrase d'union personnalisée</label>
                <textarea 
                  name="phraseUnion" 
                  value={formData.phraseUnion} 
                  onChange={handleChange} 
                  placeholder="Laissez vide pour utiliser la phrase par défaut" 
                  rows={3}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  Par défaut : "ENTOURÉS DE LEURS PROCHES, ILS SCELLERONT LEUR UNION DANS LA JOIE ET LE BONHEUR"
                </small>
              </div>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7z" fill="currentColor"/>
                </svg>
                Thème
              </h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Background</label>
                  <input type="text" name="background" value={formData.background} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Heart</label>
                  <input type="text" name="heart" value={formData.heart} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Text</label>
                  <input type="text" name="text" value={formData.text} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Accent</label>
                  <input type="text" name="accent" value={formData.accent} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Overlay</label>
                  <input type="text" name="overlay" value={formData.overlay} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Box</label>
                  <input type="text" name="box" value={formData.box} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Accent Line</label>
                  <input type="text" name="accentLine" value={formData.accentLine} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Invite Box Background</label>
                  <input type="text" name="inviteBoxBackground" value={formData.inviteBoxBackground} onChange={handleChange} />
                </div>
              </div>
              
              <div className="form-group">
                <label>Sections Background</label>
                <input type="text" name="sectionsBackground" value={formData.sectionsBackground} onChange={handleChange} />
              </div>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Boissons sans alcool
              </h4>
              {formData.boissonsSansAlcool.map((boisson: string, index: number) => (
                <div key={index} className="array-item">
                  <input
                    type="text"
                    value={boisson}
                    onChange={(e) => handleArrayChange("boissonsSansAlcool", index, e.target.value)}
                    placeholder="ex: Jus d'orange"
                  />
                  <button type="button" onClick={() => removeArrayItem("boissonsSansAlcool", index)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 13H5v-2h14v2z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem("boissonsSansAlcool")} className="add-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Ajouter
              </button>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Boissons avec alcool
              </h4>
              {formData.boissonsAvecAlcool.map((boisson: string, index: number) => (
                <div key={index} className="array-item">
                  <input
                    type="text"
                    value={boisson}
                    onChange={(e) => handleArrayChange("boissonsAvecAlcool", index, e.target.value)}
                    placeholder="ex: Champagne"
                  />
                  <button type="button" onClick={() => removeArrayItem("boissonsAvecAlcool", index)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 13H5v-2h14v2z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem("boissonsAvecAlcool")} className="add-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Ajouter
              </button>

              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/>
                </svg>
                Images
              </h4>
              <div className="form-group">
                <label>Photo de fond</label>
                <input
                  type="file"
                  name="photoBackground"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFormData({ ...formData, photoBackground: e.target.files[0] });
                    }
                  }}
                />
              </div>
              <div className="form-group">
                <label>Image d'invitation</label>
                <input
                  type="file"
                  name="invitationImage"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFormData({ ...formData, invitationImage: e.target.files[0] });
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleSubmit} className="save-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                </svg>
                Enregistrer
              </button>
              <button className="close-btn" onClick={() => setEditMode(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                </svg>
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MariageOptionsModal;